import { TableComponents, TableVirtuoso } from "react-virtuoso";
import { TableData, TableHeader } from "./DataTable";
import { Dispatch, SetStateAction, MouseEvent, forwardRef, ChangeEvent, useRef, useState, useEffect } from "react";
import { Skeleton } from "@mui/material";
import StatsCell from "./CellTypes/StatsCell";
import StructureCell from "./CellTypes/StructureCell";
import TagCell from "./CellTypes/TagCell";
import BooleanCell from "./CellTypes/BooleanCell";
import StatusCell from "./CellTypes/StatusCell";
import RolesCell from "./CellTypes/RolesCell";
import TextCell from "./CellTypes/TextCell";
import { convertNumberToDecimal } from "../../utils/formattingFns";
import CustomCheckbox from "../Form/CustomCheckbox";
import NestedDropdown from "../Menus&Dropdowns/NestedDropdown";
import Dropdown from "../Menus&Dropdowns/Dropdown";
import { useNavigate } from "react-router-dom";
import TableHeaderCell from "./CellTypes/TableHeaderCell";
import TableBodyCell from "./CellTypes/TableBodyCell";

import { ReactComponent as MlclIcon } from '../../assets/Icons/adornments/molecule-thin.svg';
import { ReactComponent as ChevronRightIcon } from '../../assets/Icons/chevron/Chevron-Right.svg';
import NoResultsImage from '../../assets/Images/noResultsFound.png';
import EmptyTable, { DataTypes } from "../Alerts/EmptyTable";


interface VirtualTableContext {
    isLoading: boolean;
    paramValues: any[] | undefined;
    selection: {
        selectedItems: (string | number)[],
        setSelectedItems: Dispatch<SetStateAction<(string | number)[]>>,
    } | undefined;
    headers: TableHeader[];
    tableData: TableData[];
    fixedItemHeight?: number,
    dataType?: DataTypes,
    count?: number,
    expandable?: boolean,
    expandedRows?: Set<string | number>,
    onToggleExpand?: (id: string | number) => void,
}

const VirtuosoTableComponents: TableComponents<TableData, VirtualTableContext> = {
    TableRow: forwardRef(({ item, ...props  }, ref) => {
        const index = props["data-index"];
        const { selection, expandable, expandedRows, onToggleExpand } = props.context;
        return (
            <tr
                {...props}
                className={`relative ${
                    item.isVersionRow ? 'bg-[#F5F5FA] border-l-2 border-l-[#6C63FF]/30' :
                    selection && selection.selectedItems.includes(item.id) ? 'bg-[#D9DBE7]' : 
                    index%2 === 0 ? 'even-row' : 'odd-row'
                }`}
                style={expandable && item.isGroupHeader && (item.groupCount ?? 0) > 1 ? { cursor: 'pointer' } : undefined}
                onClick={expandable && item.isGroupHeader && (item.groupCount ?? 0) > 1 ? (e) => {
                    if ((e.target as HTMLElement).closest('button, a, input, [role="menu"], [role="menuitem"]')) return;
                    onToggleExpand?.(item.id);
                } : undefined}
            >
                {props.children}
            </tr>
        )
    }),
    Table: (props) => {
        const { paramValues, tableData } = props.context;
        return (
            <table {...props} className={`${tableData.length === 0 && paramValues !== undefined && paramValues.length > 0 ? 'table-auto' : 'table-fixed'} min-w-full`}/>
        )
    },
    TableBody: forwardRef(({ children, ...props }, ref) => {
        const { isLoading, fixedItemHeight } = props.context;

        return (
        <tbody
            {...props}
            ref={ref}
        >
            {isLoading ? (
                <>
                {Array.from(Array(10).keys()).map(rowKey => (
                    <tr key={rowKey} className='table-body'>
                        {Array.from(Array(10).keys()).map(key => (
                            <td key={key} style={{ height: fixedItemHeight !== undefined ? fixedItemHeight : 'auto' }} className="py-4 px-6 w-[200px]"><Skeleton variant='text' sx={{fontSize: '1rem'}} width='100%'/></td>
                        ))}
                    </tr>
                ))}
                </>
            ) : (
                <>{children}</>
            )}
        </tbody>
        )
    }),
    EmptyPlaceholder: (props) => {
        const { isLoading, paramValues, headers, fixedItemHeight, count, dataType } = props.context;
        
        if (isLoading) {
            return (
                <tbody>
                    {Array.from(Array(10).keys()).map(rowKey => (
                        <tr key={rowKey} className='table-body'>
                            {Array.from(Array(10).keys()).map(key => (
                                <td key={key} style={{ height: fixedItemHeight !== undefined ? fixedItemHeight : 'auto' }} className="py-4 px-6 w-[200px]"><Skeleton variant='text' sx={{fontSize: '1rem'}} width='100%'/></td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            )
        } else if (paramValues !== undefined && paramValues.length > 0) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={headers.length+1} className="h-[400px]">
                            <div className="w-full flex flex-col justify-center items-center gap-6 text-default">
                                <img src={NoResultsImage} className="w-[120px]" alt='no-results-found'/>
                                <div className="flex flex-col gap-2 justify-center items-center">
                                <p className="text-[18px] font-semibold text-disable">No results found</p>
                                <p className="text-disable font-normal">Adjust your filters and try again.</p>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            )
        } else if (count !== undefined && count === 0) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={headers.length+1} className="h-[400px]">
                            <EmptyTable dataType={dataType ?? 'datafiles'}/>
                        </td>
                    </tr>
                </tbody>
            )
        } else {
            return (
                <></>
            )
        }
    },
}


export default function VirtualizedDataTable({
    isLoading,
    headers,
    tableData,
    dataType,
    paramValues,
    selection,
    statsType,
    threshold,
    sorting,
    setPopupMessage,
    moleculeHighlightingModelFamily,
    fixedItemHeight,
    count,
    expandable,
    expandedRows,
    onToggleExpand,
}: {
    isLoading: boolean,

    headers: TableHeader[],
    tableData: TableData[],
    dataType?: DataTypes,
    paramValues?: any[],
    selection?: {
        selectedItems: (string | number)[],
        setSelectedItems: Dispatch<SetStateAction<(string | number)[]>>,
    },
    statsType?: 'model' | 'prediction',
    threshold?: number,
    sorting?: {
        sortableHeaders: string[],
        sortHeaders: (e: MouseEvent<HTMLButtonElement>, value: string) => void,
        sortByParam: string | null,
        sortOrderParam: 'desc' | 'asc' | null,
    },
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,
    moleculeHighlightingModelFamily?: number | null,
    fixedItemHeight?: number,
    count?: number,
    expandable?: boolean,
    expandedRows?: Set<string | number>,
    onToggleExpand?: (id: string | number) => void,
}) {
    const tableContainerRef = useRef<any | null>(null);
    const [ isScrollable, setIsScrollable ] = useState(false);
    const [ atLeft, setAtLeft ] = useState(true);
    const [ atRight, setAtRight ] = useState(false);
    const [ height, setHeight ] = useState('100%');

    const navigate = useNavigate();


    useEffect(() => {
            let ht = '100%';

            if (fixedItemHeight !== undefined && tableData.length > 0) {
                const tableHeight = 51 + (tableData.length * fixedItemHeight);
                ht = `${tableHeight}px`;
            } else if (count !== undefined && count === 0) {
                ht = `451px`;
            }
            setHeight(ht)
    }, [fixedItemHeight, tableData, count]);

    useEffect(() => {
        const tableContainer = tableContainerRef.current;

        const checkScroll = () => {
            if (!tableContainer) return;
            const { scrollLeft, scrollWidth, clientWidth } = tableContainer;
            setIsScrollable(scrollWidth > clientWidth);
            setAtLeft(scrollLeft === 0);
            setAtRight(scrollLeft + clientWidth >= scrollWidth - 1);
        }

        checkScroll();

        const resizeObserver = new ResizeObserver(checkScroll);
        if (tableContainer) {
            resizeObserver.observe(tableContainer);
        }

        tableContainer?.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);

        return () => {
            resizeObserver.disconnect();
            tableContainer?.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        }
    }, []);

    const handleItemSelection = (e:ChangeEvent<HTMLInputElement>) => {
        if (selection) {
            const { selectedItems, setSelectedItems } = selection;
            const id = e.target.value;
            const isNum = typeof tableData[0].id === 'number';

            if (e.target.checked) {
                setSelectedItems(selectedItems.concat(isNum ? Number(id) : id));
            } else {
                setSelectedItems(selectedItems.filter(item => item !== (isNum ? Number(id) : id)));
            }
        }
    };

    const handleAllItemSelection = (e:ChangeEvent<HTMLInputElement>) => {
        if (selection) {
            const { setSelectedItems } = selection;
            if (e.target.checked) {
                setSelectedItems(tableData.map(data => data.id))
            } else {
                setSelectedItems([]);
            }
        }
    }

    const navigateToMoleculeHighlightingPage = (data: TableData) => {
        const fields = data.fields;
        if (fields.length > 0) {
            const highlightingField = fields.find(field => field.associatedColumn === 'molecule_highlighting');

            if (highlightingField) {
                navigate(`prediction/${data.id}/${encodeURIComponent(highlightingField.value as string)}`)
            }
        }
    }

    function fixedHeaderContent() {
        return (
            <tr className="bg-secondary text-default relative z-20">
                {isLoading ? (
                    <>
                    {Array.from(Array(10).keys()).map(key => (
                        <td key={key} className="py-4 px-6"><Skeleton variant='text' sx={{fontSize: '1rem'}} width='100%'/></td>
                    ))}
                    </>
                ) : (
                    <>
                    {headers.filter(header => header.isShown && header.value !== 'molecule_highlighting').map((header,i) => (
                        <TableHeaderCell 
                            key={i}
                            header={header}
                            isSticky={i === 0}
                            isShadowShown={i === 0 && isScrollable && !atLeft}
                            hasMoleculeHighlighting={i === 0 && headers.find(header => header.value === 'molecule_highlighting') ? true : false}
                            sorting={sorting}
                            checkboxComp={i === 0 && selection ? (
                                <CustomCheckbox
                                    indeterminate={(selection.selectedItems.length !== tableData.length) && selection.selectedItems.length > 0}
                                    checked={selection.selectedItems.length === tableData.length && tableData.length > 0}
                                    handleChange={handleAllItemSelection}
                                />
                            ) : undefined}
                        />
                    ))}
                    {tableData.length > 0 && tableData[0].options && (
                        <th
                            className={`bg-secondary sticky right-0 ${isScrollable && !atRight ? 'shadow-right' : ''}`}
                        >
                            <div className={`py-4 px-6`}></div>
                        </th>
                    )}
                    </>
                )}
            </tr>
        )
    }

    function rowContent(index: number, row: TableData) {
        const { id, fields, options } = row;
        const moleculeHighlightingHeader = headers.find(header => header.value === 'molecule_highlighting');
        const moleculeHighlightingField = fields.find(field => field.associatedColumn === 'molecule_highlighting');

        return (
            <>
            {headers.filter(header => header.value !== 'molecule_highlighting').map((header, headerIndex) => {
                const matchingData = fields.find(field => field.associatedColumn === header.value);
                if (matchingData && header.isShown) {
                    if (matchingData.type === 'stat' && statsType !== undefined) {
                        return (
                            <StatsCell 
                                key={headerIndex}
                                value={matchingData.value as string}
                                name={matchingData.associatedColumn}
                                statsType={statsType}
                                isSelected={selection && selection.selectedItems.includes(id) ? true : false}
                                threshold={threshold}
                            />
                        )
                    } else {
                        return (
                        <TableBodyCell
                            key={headerIndex}
                            fixedItemHeight={fixedItemHeight}
                            isSticky={headerIndex === 0}
                            isShadowShown={isScrollable && !atLeft}
                            alignment={matchingData.alignment}
                            expandComp={headerIndex === 0 && expandable ? (
                                row.isGroupHeader && (row.groupCount ?? 0) > 1 ? (
                                    <button 
                                        onClick={(e:any) => {
                                            e.stopPropagation();
                                            onToggleExpand?.(row.id);
                                        }}
                                        className={`w-[16px] h-[16px] transition-transform duration-200 text-disable hover:text-primary ${
                                            expandedRows?.has(row.id) ? 'rotate-90' : 'rotate-0'
                                        }`}
                                    >
                                        <ChevronRightIcon />
                                    </button>
                                ) : row.isVersionRow ? (
                                    <div className="w-[16px] h-[16px]" />
                                ) : undefined
                            ) : undefined}
                            checkboxComp={headerIndex === 0 && selection ? (
                                <CustomCheckbox
                                    checked={selection.selectedItems.includes(id)}
                                    handleChange={handleItemSelection}
                                    value={id}
                                />
                            ) : undefined}
                            moleculeHighlightingComp={headerIndex === 0 && moleculeHighlightingHeader ? (
                                <>
                                <div className="w-[20px] h-full flex justify-center items-center">
                                    {moleculeHighlightingField && moleculeHighlightingField.value !== '' && (
                                        <button 
                                            onClick={(e:any) => navigateToMoleculeHighlightingPage(row)} 
                                            className='w-[20px] text-disable hover:text-primary transition-all'
                                        >
                                            <MlclIcon />
                                        </button>
                                    )}
                                </div>
                                </>
                            ) : undefined}

                        >
                            {matchingData.type === 'structure' && (header.label.toLocaleLowerCase().includes('structure') || header.label.toLocaleLowerCase().includes('compound')) ? (
                                <StructureCell
                                    index={headerIndex}
                                    data={row}
                                    value={matchingData.value as string}
                                    threshold={threshold}
                                    setPopupMessage={setPopupMessage}
                                    moleculeHighlightingModelFamily={moleculeHighlightingModelFamily}
                                    id={`molecule-structure-svg-${id}`}
                                    hasPadding={false}
                                />
                            ) : matchingData.type === 'tags' ? (
                                <TagCell 
                                    value={matchingData.value as string[]}
                                    label={header.label}
                                    hasPadding={false}
                                />
                            ) : matchingData.type === 'boolean' ? (
                                <BooleanCell 
                                    value={matchingData.value as string}
                                />
                            ) : matchingData.type === 'status' ? (
                                <StatusCell 
                                    value={matchingData.value as string}
                                />
                            ) : matchingData.type === 'roles' ? (
                                <RolesCell 
                                    value={matchingData.value as string[]}
                                />
                            ) : (
                                <>
                                <TextCell 
                                    value={convertNumberToDecimal(matchingData.value as string | number)}
                                    alignment={matchingData.alignment}
                                    action={matchingData.action}
                                    headerIndex={1}
                                    hasPadding={false}
                                />
                                {expandable && headerIndex === 0 && row.isGroupHeader && (row.groupCount ?? 0) > 1 && (
                                    <span className="ml-2 px-2 py-0.5 text-[11px] rounded-full bg-[#E8E9F3] text-primary font-medium whitespace-nowrap">
                                        {row.groupCount} {row.groupCount === 1 ? 'version' : 'versions'}
                                    </span>
                                )}
                                </>
                            )}
                        </TableBodyCell>
                        )
                    }
                    
                } else {
                    return null;
                }
            })}
            {options && (
                <td
                    className={`${selection && selection.selectedItems.includes(id) && 'bg-[#D9DBE7]'} relative sticky right-0 bg-inherit z-10 ${isScrollable && !atRight ? 'shadow-right' : ''}`}
                >
                    <div className="py-4 px-6 flex justify-end items-center relative">
                        {options.find(opt => opt.moreOptions !== undefined) ? (
                            <NestedDropdown 
                                options={options}
                                dir="left"
                            />
                        ) : (
                            <Dropdown  
                                options={options}
                            />
                        )}
                    </div>
                </td>
            )}
            </>
        )
    }

    

    return (
        <div className="w-full grow overflow-y-hidden">
            <div style={{ maxHeight: '100%', height }} className={`w-full transition-all bg-white rounded-xl overflow-hidden border border-outline`}>
                <TableVirtuoso 
                    style={{ width: '100%' }}
                    data={tableData}
                    components={VirtuosoTableComponents}
                    fixedHeaderContent={fixedHeaderContent}
                    itemContent={rowContent}
                    scrollerRef={(ref) => {
                        tableContainerRef.current = ref;
                    }}
                    context={{
                        isLoading,
                        paramValues,
                        selection,
                        headers,
                        tableData,
                        fixedItemHeight,
                        dataType,
                        count,
                        expandable,
                        expandedRows,
                        onToggleExpand,
                    }}
                    fixedItemHeight={fixedItemHeight}
                />
            </div>
        </div>
    )
}